import {
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import type { ContentItem } from '../types/content';

interface SlideCardProps {
  item: ContentItem;
  onEdit: () => void;
  onDelete: () => void;
}

export default function SlideCard({ item, onEdit, onDelete }: SlideCardProps) {
  const renderSlidePreview = () => {
    try {
      const slide = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
      const backgroundUrl = slide.background?.url || '';

      return (
        <div className="relative h-32 rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
          {backgroundUrl && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-50"
              style={{ backgroundImage: `url(${backgroundUrl})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="relative h-full p-4 flex flex-col justify-end">
            <div className="space-y-1">
              {slide.subtitle && (
                <span className="inline-block bg-golf-orange px-2 py-0.5 rounded text-xs font-semibold text-white">
                  {slide.subtitle}
                </span>
              )}
              <h4 className="text-white font-bold text-sm line-clamp-1">
                {slide.title || 'Untitled'}
              </h4>
              <p className="text-gray-300 text-xs line-clamp-1">
                {slide.description || 'No description'}
              </p>
            </div>
          </div>
          {slide.designVariant && (
            <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm px-2 py-1 rounded text-xs text-white capitalize">
              {slide.designVariant}
            </div>
          )}
        </div>
      );
    } catch (e) {
      return (
        <div className="h-32 rounded-lg bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <PhotoIcon className="w-8 h-8 mx-auto mb-2" />
            <span className="text-xs">Invalid slide format</span>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="group border border-gray-200 rounded-xl hover:border-golf-orange/40 hover:shadow-md transition-all overflow-hidden">
      <div className="cursor-pointer" onClick={onEdit}>
        {renderSlidePreview()}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-900 truncate flex-1 mr-2">
            {item.title}
          </h4>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full shrink-0 ${
            item.status === 'Published'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {item.status === 'Published' ? (
              <CheckCircleIcon className="w-3 h-3" />
            ) : (
              <ClockIcon className="w-3 h-3" />
            )}
            {item.status}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <CalendarIcon className="w-3.5 h-3.5" />
            {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : item.lastUpdated || '-'}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
